import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";

import { Box, Typography, List, ListItem, IconButton } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import recruiterTheme, { FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW } from '../../styles/recruiterTheme';
import axios from 'axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ScreeningTestDetails = ({screeningID}) => {

    const [screeningTest, setScreeningTest] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const getScreeningTest = async () => {
            try {
                const response = await axios.post(`${process.env.REACT_APP_SCREENING_ID_API}/${screeningID}`, { screeningID });                
                setScreeningTest(response.data);
            } catch (error) {
                setError('Error fetching screening test data');
            } finally {
                setLoading(false);
            }
        };

        getScreeningTest();
    }, [screeningID]);

    const handleBack = () => {
        if (location.state && location.state.fromEditJobDescription) {
          navigate(-1); 
        } else {
          navigate(-0); 
        }
      };

    const stateBox = (text, color = COLORS.muted) => (
        <ThemeProvider theme={recruiterTheme}>
            <Box sx={{ py: 5, px: 3, textAlign: 'center', fontFamily: FONT_BODY }}>
                <Typography sx={{ fontSize: TYPE.body, lineHeight: 1.5, color }}>{text}</Typography>
            </Box>
        </ThemeProvider>
    );

    if (loading) return stateBox('Loading...');
    if (error) return stateBox(error, COLORS.error);
    if (!screeningTest) return stateBox('No data available');

    return (
        <ThemeProvider theme={recruiterTheme}>
        <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: FONT_BODY, color: COLORS.ink }}>
            {/* Header: back button + title on the left */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                    <IconButton onClick={handleBack} aria-label="Back"
                        sx={{ width: 40, height: 40, borderRadius: `${RADIUS.sm}px`, color: COLORS.primaryDark, background: COLORS.accentSoft, '&:hover': { background: COLORS.border } }}>
                        <ArrowBackIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography component="h1" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.page, letterSpacing: '-0.02em', lineHeight: 1.25, color: COLORS.primaryDeep }}>
                            Screening Test Details
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 0.5 }}>
                            <Typography sx={{ fontSize: TYPE.body, fontWeight: 600, lineHeight: 1.5, color: COLORS.primary }}>Job Role: {screeningTest.jobRole}</Typography>
                            <Typography sx={{ fontSize: TYPE.body, fontWeight: 600, lineHeight: 1.5, color: COLORS.primary }}>Passing Score: {screeningTest.passingScore}</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ border: `1px solid ${COLORS.accentSoft}`, borderRadius: `${RADIUS.lg}px`, background: COLORS.surface, boxShadow: SHADOW.card, overflow: 'hidden', mb: 5 }}>
            <List disablePadding>
                {screeningTest.questions.map((question, qIdx) => (
                    <ListItem key={question._id} divider={qIdx < screeningTest.questions.length - 1} sx={{ px: 3, py: 2, alignItems: 'flex-start' }}>
                        <Box sx={{ minWidth: 0, width: '100%' }}>
                            <Typography sx={{ fontSize: TYPE.cardTitle, fontWeight: 700, lineHeight: 1.25, color: COLORS.primaryDeep, mb: 1 }}>{question.questionText}</Typography>
                            <List dense disablePadding sx={{ mb: 1 }}>
                                {question.answerOptions.map((option, index) => (
                                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                        <Typography sx={{ fontSize: TYPE.body, lineHeight: 1.5 }}>{option}</Typography>
                                    </ListItem>
                                ))}
                            </List>
                            <Typography sx={{ fontSize: TYPE.small, lineHeight: 1.5, color: COLORS.muted }}>
                                <strong>Correct Answer:</strong> {question.correctAnswer}
                            </Typography>
                        </Box>
                    </ListItem>
                ))}
            </List>
            </Box>
        </Box>
        </ThemeProvider>
    );
};

export default ScreeningTestDetails;
